import React, { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import BackIcon from "../../images/BackIcon";
import CloseIcon from "../../images/CloseIcon";
import { useDialogFocus } from "../../hook/useFormKeyboard";
import {
  BORDER_SOFT,
  BORDER_STRONG,
  CHIP_TEAL_BG,
  DESTRUCTIVE,
  TEAL,
  TEXT,
  TEXT_DIM,
  TEXT_MUTED,
  t,
} from "../../components/list";
import { BottomSheet, StickyFooter } from "../../components/mobile";
import { LeaveProceed, installLeaveGuard } from "../../services/leaveGuard";

export const INK = "#05292A";
export const SURFACE_SOFT = "#F9FBFB";

// Hover and focus need real CSS (inline <style>: the host drops module-only classes).
export const ROLE_CSS = `
.role-btn { background-color: #FFFFFF; }
.role-btn:not(:disabled):hover { background-color: #EBF9F9; }
.role-link span { color: #4A5353; }
.role-link:hover span { color: #0F7679; }
.role-row:hover { background-color: #F5FAFA; }
.role-text-btn:not(:disabled):hover { text-decoration: underline; }
.role-option:hover, .role-option:focus-visible { background-color: #F5FAFA; }
.role-btn:focus-visible, .role-link:focus-visible, .role-switch:focus-visible, .role-check:focus-visible,
.role-tab:focus-visible, .role-text-btn:focus-visible, .role-primary:focus-visible, .role-option:focus-visible,
.role-disclosure:focus-visible { outline: 2px solid #0F7679; outline-offset: 2px; }
.role-field:focus { border-color: #0F7679 !important; box-shadow: 0 0 0 2px #0F76791F; outline: none; }
@keyframes role-pop { from { opacity: 0; transform: translateY(8px) scale(.98); } to { opacity: 1; transform: none; } }
.role-pop { animation: role-pop 200ms cubic-bezier(.2,.8,.2,1) both; }
@media (prefers-reduced-motion: reduce) { .role-pop { animation: none; } }
`;

export const RoleStyles = () => <style>{ROLE_CSS}</style>;

export const PANEL: React.CSSProperties = {
  border: `1px solid ${BORDER_SOFT}`,
  borderRadius: "16px",
  backgroundColor: "#FFFFFF",
  minWidth: 0,
};

export const H2: React.CSSProperties = { margin: 0, fontSize: "16px", fontWeight: 600, color: INK, lineHeight: "24px" };
export const NUM: React.CSSProperties = { fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };

/** Desktop text field; the touch layout passes the kit's 48px / 16px variant. */
export const fieldStyle = (touch: boolean, invalid?: boolean): React.CSSProperties => ({
  display: "block",
  width: "100%",
  boxSizing: "border-box",
  height: touch ? "48px" : "44px",
  padding: touch ? "0 14px" : "0 12px",
  fontSize: touch ? "16px" : "14px",
  lineHeight: "20px",
  color: TEXT,
  backgroundColor: "#FFFFFF",
  border: `1px solid ${invalid ? DESTRUCTIVE : BORDER_STRONG}`,
  borderRadius: "12px",
  fontFamily: "Inter, sans-serif",
});

export const textAreaStyle = (touch: boolean, invalid?: boolean): React.CSSProperties => ({
  ...fieldStyle(touch, invalid),
  height: "88px",
  padding: touch ? "12px 14px" : "10px 12px",
  resize: "vertical",
});

export const Label: React.FC<{ htmlFor: string; required?: boolean; children: React.ReactNode }> = ({
  htmlFor,
  required,
  children,
}) => (
  <label
    htmlFor={htmlFor}
    style={{ display: "block", fontSize: "14px", lineHeight: "20px", fontWeight: 500, color: TEXT_MUTED, marginBottom: "8px" }}
  >
    {children}
    {required && <span style={{ color: DESTRUCTIVE }}> *</span>}
  </label>
);

export const FieldHint: React.FC<{ id?: string; children: React.ReactNode }> = ({ id, children }) => (
  <p id={id} style={{ margin: "6px 0 0", fontSize: "13px", lineHeight: "18px", color: TEXT_DIM }}>
    {children}
  </p>
);

export const FieldErrorText: React.FC<{ id?: string; children: React.ReactNode }> = ({ id, children }) => (
  <p id={id} role="alert" style={{ margin: "6px 0 0", fontSize: "13px", lineHeight: "18px", color: DESTRUCTIVE }}>
    {children}
  </p>
);

export const BackLink: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <button
    type="button"
    className="role-link"
    onClick={onClick}
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      alignSelf: "flex-start",
      height: "32px",
      padding: "0 8px 0 2px",
      border: "none",
      borderRadius: "8px",
      background: "none",
      cursor: "pointer",
    }}
  >
    <BackIcon />
    <span style={{ fontSize: "14px", fontWeight: 500 }}>{label}</span>
  </button>
);

export type BadgeKind = "teal" | "off" | "warn" | "ok" | "danger";

const BADGE_COLORS: Record<BadgeKind, { bg: string; fg: string }> = {
  teal: { bg: CHIP_TEAL_BG, fg: TEAL },
  off: { bg: "#F2F4F3", fg: "#606060" },
  warn: { bg: "#FBF1DC", fg: "#6F4C00" },
  ok: { bg: "#E4F1EA", fg: "#1F6B4A" },
  danger: { bg: "#F6E0E0", fg: DESTRUCTIVE },
};

export const Badge: React.FC<{ label: string; kind?: BadgeKind }> = ({ label, kind = "off" }) => {
  const c = BADGE_COLORS[kind];
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: "999px",
        backgroundColor: c.bg,
        color: c.fg,
        fontSize: "12px",
        fontWeight: 500,
        lineHeight: "18px",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
};

// Space toggles a switch or checkbox as on a native control; Enter must not.
const blockEnter = (e: React.KeyboardEvent<HTMLButtonElement>) => {
  if (e.key === "Enter") e.preventDefault();
};

export const Switch: React.FC<{
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  label: string;
  describedBy?: string;
  touch?: boolean;
}> = ({ checked, onChange, disabled, label, describedBy, touch }) => (
  <button
    type="button"
    role="switch"
    className="role-switch"
    aria-checked={checked}
    aria-label={label}
    aria-describedby={describedBy}
    disabled={disabled}
    onKeyDown={blockEnter}
    onClick={(e) => {
      e.stopPropagation();
      if (!disabled) onChange(!checked);
    }}
    style={{
      position: "relative",
      flex: "none",
      width: touch ? "48px" : "40px",
      height: touch ? "28px" : "24px",
      borderRadius: "999px",
      border: `1.5px solid ${checked ? TEAL : "#6A7878"}`,
      backgroundColor: checked ? TEAL : "#FFFFFF",
      opacity: disabled ? 0.45 : 1,
      cursor: disabled ? "not-allowed" : "pointer",
      padding: 0,
      transition: "background-color 150ms ease, border-color 150ms ease",
    }}
  >
    <span
      aria-hidden="true"
      style={{
        position: "absolute",
        top: "50%",
        left: checked ? (touch ? "23px" : "19px") : "3px",
        width: touch ? "19px" : "15px",
        height: touch ? "19px" : "15px",
        marginTop: touch ? "-9.5px" : "-7.5px",
        borderRadius: "999px",
        backgroundColor: checked ? "#FFFFFF" : "#6A7878",
        transition: "left 150ms ease",
      }}
    />
  </button>
);

const CheckMark = () => (
  <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="#FFFFFF" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 10.5l4 4 8-8.5" />
  </svg>
);

/** Group checkbox: all on, some on (mixed) or none. A click turns everything on, or off when all are on. */
export const TriCheck: React.FC<{
  state: "all" | "some" | "none";
  onChange: (next: boolean) => void;
  disabled?: boolean;
  label: string;
}> = ({ state, onChange, disabled, label }) => (
  <button
    type="button"
    role="checkbox"
    className="role-check"
    aria-checked={state === "all" ? true : state === "some" ? "mixed" : false}
    aria-label={label}
    disabled={disabled}
    onKeyDown={blockEnter}
    onClick={() => !disabled && onChange(state !== "all")}
    style={{
      flex: "none",
      width: "20px",
      height: "20px",
      borderRadius: "6px",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 0,
      backgroundColor: state === "none" ? "#FFFFFF" : TEAL,
      border: state === "none" ? "1.5px solid #6A7878" : `1.5px solid ${TEAL}`,
      opacity: disabled ? 0.45 : 1,
      cursor: disabled ? "not-allowed" : "pointer",
    }}
  >
    {state === "all" && <CheckMark />}
    {state === "some" && <span aria-hidden="true" style={{ width: "10px", height: "2px", borderRadius: "2px", backgroundColor: "#FFFFFF" }} />}
  </button>
);

export const TextButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({ style, children, ...rest }) => (
  <button
    type="button"
    className="role-text-btn"
    {...rest}
    style={{
      border: "none",
      background: "none",
      padding: "4px 6px",
      borderRadius: "6px",
      fontSize: "13px",
      fontWeight: 500,
      color: rest.disabled ? TEXT_DIM : TEAL,
      cursor: rest.disabled ? "not-allowed" : "pointer",
      ...style,
    }}
  >
    {children}
  </button>
);

type PillVariant = "primary" | "outline" | "danger";

export const PillButton = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: PillVariant; small?: boolean }
>(({ variant = "outline", small, style, children, className, ...rest }, ref) => {
  const solid = variant !== "outline";
  return (
    <button
      ref={ref}
      type="button"
      className={`${solid ? "role-primary" : "role-btn"}${className ? " " + className : ""}`}
      {...rest}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
        height: small ? "36px" : "40px",
        padding: small ? "0 16px" : "0 20px",
        borderRadius: "999px",
        border: solid ? "none" : `1px solid ${BORDER_STRONG}`,
        backgroundColor: variant === "primary" ? TEAL : variant === "danger" ? DESTRUCTIVE : undefined,
        color: solid ? "#FFFFFF" : TEXT,
        fontSize: "14px",
        fontWeight: 500,
        whiteSpace: "nowrap",
        opacity: rest.disabled ? 0.5 : 1,
        cursor: rest.disabled ? "not-allowed" : "pointer",
        ...style,
      }}
    >
      {children}
    </button>
  );
});
PillButton.displayName = "PillButton";

export interface DialogAction {
  label: string;
  onClick: () => void;
  variant?: PillVariant;
  disabled?: boolean;
}

/** Centred dialog on desktop, bottom sheet on touch. Escape and the backdrop close it. */
export const RoleDialog: React.FC<{
  open: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  touch: boolean;
  language: any;
  children?: React.ReactNode;
  /** The last one is the primary; the first is the safe choice. */
  actions?: DialogAction[];
}> = ({ open, title, subtitle, onClose, touch, language, children, actions = [] }) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const pressedBackdrop = useRef(false);
  useDialogFocus(open && !touch, dialogRef, onClose);

  if (!open) return null;
  if (touch) {
    const primary = actions[actions.length - 1];
    const secondary = actions.length > 1 ? actions[0] : undefined;
    return (
      <BottomSheet
        open={open}
        onClose={onClose}
        title={title}
        footer={
          primary ? (
            <StickyFooter
              position="static"
              primaryLabel={primary.label}
              onPrimary={primary.onClick}
              primaryDisabled={primary.disabled}
              destructive={primary.variant === "danger"}
              cancelLabel={secondary?.label}
              onCancel={secondary?.onClick}
            />
          ) : undefined
        }
      >
        <div style={{ padding: "0 20px 16px", fontSize: "15px", lineHeight: "22px", color: TEXT_MUTED }}>
          {subtitle && <p style={{ margin: "0 0 12px", fontSize: "13px", color: TEXT_DIM }}>{subtitle}</p>}
          {children}
        </div>
      </BottomSheet>
    );
  }
  return createPortal(
    <div
      onMouseDown={(e) => {
        pressedBackdrop.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (pressedBackdrop.current && e.target === e.currentTarget) onClose();
        pressedBackdrop.current = false;
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        backgroundColor: "rgba(5,41,42,.40)",
        fontFamily: "Inter, sans-serif",
      }}
    >
      <RoleStyles />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="role-pop"
        style={{
          width: "100%",
          maxWidth: "460px",
          maxHeight: "min(720px, calc(100vh - 48px))",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#FFFFFF",
          borderRadius: "16px",
          boxShadow: "0 24px 60px rgba(5,41,42,.22)",
          outline: "none",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "12px",
            padding: "18px 20px 14px 24px",
            borderBottom: `1px solid ${BORDER_SOFT}`,
            flex: "none",
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 id={titleId} style={{ margin: 0, fontSize: "18px", fontWeight: 600, color: INK, lineHeight: "26px", overflowWrap: "anywhere" }}>
              {title}
            </h2>
            {subtitle && <p style={{ margin: "2px 0 0", fontSize: "13px", color: TEXT_DIM }}>{subtitle}</p>}
          </div>
          <button
            type="button"
            className="role-btn"
            onClick={onClose}
            aria-label={t(language, "Close", "Close")}
            style={{
              width: "36px",
              height: "36px",
              borderRadius: "999px",
              border: "none",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flex: "none",
              cursor: "pointer",
              marginRight: "-6px",
            }}
          >
            <CloseIcon />
          </button>
        </div>
        <div style={{ overflowY: "auto", padding: "20px 24px", fontSize: "14px", lineHeight: "22px", color: TEXT_MUTED, minHeight: 0 }}>
          {children}
        </div>
        {actions.length > 0 && (
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", padding: "0 24px 24px", flex: "none" }}>
            {actions.map((action) => (
              <PillButton key={action.label} variant={action.variant ?? "outline"} disabled={action.disabled} onClick={action.onClick}>
                {action.label}
              </PillButton>
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

/** Warns before a reload or tab close while there are unsaved changes; `liveDirty` false (just discarded) skips it. */
export const useBeforeUnload = (dirty: boolean, liveDirty?: { current: boolean }) => {
  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (liveDirty && !liveDirty.current) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);
};

/** Opens the page's leave dialog instead of letting the host switch company or leave while dirty. */
export const useHostLeaveGuard = (dirtyRef: { current: boolean }, onBlocked: (proceed: LeaveProceed) => void) => {
  const handler = useRef(onBlocked);
  handler.current = onBlocked;
  useEffect(() => installLeaveGuard(document, () => dirtyRef.current, (proceed) => handler.current(proceed)), []);
};

/** "+Sales, −Assets, Name" with at most `max` items and a "+N more" tail. */
export const changeSummary = (items: string[], language: any, max = 4): string => {
  if (items.length <= max) return items.join(", ");
  const more = t(language, "+{n} more", "+{n} more").replace("{n}", String(items.length - max));
  return `${items.slice(0, max).join(", ")} ${more}`;
};

/** Desktop save bar, sticky to the bottom of the page's scroll area. */
export const UnsavedBar: React.FC<{
  language: any;
  dirty: boolean;
  count: number;
  summary: string;
  error?: string;
  onDiscard: () => void;
  saveButton: React.ReactNode;
}> = ({ language, dirty, count, summary, error, onDiscard, saveButton }) => (
  <div
    style={{
      position: "sticky",
      bottom: 0,
      zIndex: 5,
      display: "flex",
      alignItems: "center",
      gap: "16px",
      flexWrap: "wrap",
      padding: "14px 0",
      marginTop: "auto",
      backgroundColor: "#FFFFFF",
      borderTop: `1px solid ${BORDER_SOFT}`,
    }}
  >
    <div style={{ flex: "1 1 240px", minWidth: 0 }} aria-live="polite">
      {error ? (
        <p role="alert" style={{ margin: 0, fontSize: "14px", fontWeight: 500, color: DESTRUCTIVE }}>
          {error}
        </p>
      ) : dirty ? (
        <p style={{ margin: 0, fontSize: "14px", color: TEXT_MUTED, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          <strong style={{ fontWeight: 600, color: TEXT }}>
            {count === 1
              ? t(language, "1 unsaved change", "1 unsaved change")
              : t(language, "{n} unsaved changes", "{n} unsaved changes").replace("{n}", String(count))}
          </strong>
          {summary ? <span style={{ color: TEXT_DIM }}>{` · ${summary}`}</span> : null}
        </p>
      ) : (
        <p style={{ margin: 0, fontSize: "14px", color: TEXT_DIM }}>{t(language, "No unsaved changes", "No unsaved changes")}</p>
      )}
    </div>
    <div style={{ display: "flex", gap: "12px", flex: "none" }}>
      <PillButton onClick={onDiscard} disabled={!dirty}>
        {t(language, "Discard", "Discard")}
      </PillButton>
      {saveButton}
    </div>
  </div>
);

/** Inline notice block (landing page cleared, read-only, copy done). */
export const Notice: React.FC<{ children: React.ReactNode; tone?: "info" | "warn" }> = ({ children, tone = "info" }) => (
  <div
    role="status"
    style={{
      padding: "12px 16px",
      borderRadius: "12px",
      backgroundColor: tone === "warn" ? "#FBF1DC" : SURFACE_SOFT,
      border: `1px solid ${tone === "warn" ? "#F0DDB0" : BORDER_SOFT}`,
      fontSize: "14px",
      lineHeight: "20px",
      color: tone === "warn" ? "#6F4C00" : TEXT_MUTED,
    }}
  >
    {children}
  </div>
);
